<template>
  <Popover
    :fixed="fixed"
    :show-arrow="showArrow"
    :trigger="trigger"
    :should-close-on-external-click="shouldCloseOnExternalClick"
    :hide-delay="hideDelay"
    :placement="placement"
    :same-width="sameWidth"
    :bs="bs.popperBs"
    :is-shown="isShown"
    v-bind="popoverProps"
  >
    <!-- @slot 自定义 按钮 -->
    <slot name="button">
      <BeerButton
        icon-right="arrow-down-m"
        v-bind="$attrs"
      >
        {{ label }}
      </BeerButton>
    </slot>
    <template #popover="{ toggle }">
      <NewBsBox :bs="bs.layerBs">
        <slot
          :items="items"
          :toggle="toggle"
        >
          <Option
            v-for="o of items"
            :key="o.id"
            :disabled="o.raw?.disabled"
            @click="e => handleClickItem(e, o.raw, toggle)"
          >
            {{ o.text }}
          </Option>
        </slot>

      </NewBsBox>
    </template>
  </Popover>
</template>

<script lang="ts">
  import { defineComponent, PropType, computed } from 'vue'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import Option from '@xhs/yam-beer/cmp/overlayers/Option/Option.vue'
  import useOptions, {
    ItemType,
  } from '@xhs/yam-beer/compose/options/useOptions'
  import type {
    Placement,
  } from '@popperjs/core'
  import Popover from '../Popover/Popover.vue'
  import { Position } from '../../constants/index'
  import NewBsBox from '../../primitive/NewBsBox'
  import { BeerStyleObj } from '../../../core'

type Trigger = 'hover' | 'click'

  export default defineComponent({
    name: 'Dropdown',
    components: {
      BeerButton,
      Option,
      Popover,
      NewBsBox,
    },
    inheritAttrs: false,
    props: {
      /**
       * 按钮的文字内容
       */
      label: String,
      /**
       * 固定选项列表
       */
      options: {
        type: Array as PropType<ItemType[]>,
        default: (): ItemType[] => ([]),
      },
      /**
       * 下拉框是否是 fixed 模式
       */
      fixed: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否显示三角箭头指向对象
       */
      showArrow: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否显示，在 trigger = manual 时使用，用于外部状态控制显示
       */
      isShown: {
        type: Boolean,
        required: false,
      },
      /**
       * 触发方式
       * @type {click|hover|manual}
       */
      trigger: {
        type: String as PropType<Trigger>,
        default: 'click',
      },
      /**
       * 是否在点击外部元素后自动关闭
       */
      shouldCloseOnExternalClick: {
        type: Boolean,
        default: true,
      },
      /**
       * Hover 关闭的延时，避免过于敏感的抖动
       */
      hideDelay: {
        type: Number,
        default: 50,
      },
      /**
       * 弹层显示的方向，从 constants 模块中获取 enum
       */
      placement: {
        type: String as PropType<Placement>,
        default: Position.BOTTOM_LEFT,
      },
      popperBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否弹层宽度和对应target 一样宽
       */
      sameWidth: {
        type: Boolean,
        default: false,
      },
      /**
       * 点击 Option 后，是否自动 close dropdown
       */
      closeOnOptionClick: {
        type: Boolean,
        default: true,
      },
      /**
       * 透传给 useOption 组件的其它属性
       *
       * 详细看 Select 单选组件
       */
      optionParams: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 透传给 Popover 组件的其它属性
       */
      popoverProps: {
        type: Object,
        default: () => ({}),
      },
    },
    emits: ['click:option'],
    setup(props, { emit }) {
      const optionsHook = useOptions({
        options: computed(() => props.options),
        ...props.optionParams,
        filterFn: undefined,
      })
      const bs = computed(() => {
        const {
          overflow, width, height, size, ...poperBs
        } = props.popperBs
        return {
          poperBs,
          layerBs: {
            maxHeight: 280,
            overflow: overflow || 'auto',
            width,
            height,
            size,
          },
        }
      })

      return {
        bs,
        items: optionsHook.items,
        handleClickItem: (event: Event, item: ItemType, toggle: (val: boolean) => void) => {
          if (typeof item !== 'string' && item.disabled) {
            return
          }
          // @ts-ignore
          item.onClick?.(event, item, toggle)
          emit('click:option', event, item, toggle)
          if (props.closeOnOptionClick) {
            toggle(false)
          }
        },
      }
    },
  })
</script>
